<template>
  <div style="display: inline;margin: 0 8px;">
    <el-button @click="clickCodeConfirmButton" size="medium" type="success">
      <slot></slot>
    </el-button>
    <el-dialog :visible.sync="dialogVisible" width="355px" @close="clickDialogClose" center
               append-to-body top="188px">
      <div style="margin: 0 26px;">
        <div style="font-size: 20px;color: black;">请选择需要生成的代码：</div>
        <div style="font-size: 12px;color: gray;">温馨提示：生成代码前请确认文件路径是否正确</div>
        <!-- 生成代码选择区 -->
        <div style="max-height: 5rem;overflow-y: auto;margin-top: 0.25rem;">
          <el-checkbox :indeterminate="isIndeterminate" v-model="checkAll" @change="handleCheckAllChange">全选</el-checkbox>
          <el-checkbox-group v-model="checkedIds" @change="handleCheckedChange"
                             style="display: flex;flex-direction: column;margin-top: 5px;">
            <el-checkbox v-for="(item,index) in previewCodes" :label="item.key.split('/').pop()" :key="index">
              {{ item.key.split('/').pop() }}
            </el-checkbox>
          </el-checkbox-group>
        </div>
        <div style="width:100%; margin-top: 32px;display: flex;flex-direction: row; justify-content: center;">
          <el-button type="primary" @click="clickConfirm" size="small">确 定</el-button>
          <el-button @click="clickCancel" size="small">取 消</el-button>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { Message } from 'element-ui'
/**
 * 组织关联按钮
 *
 * @author PeterLee
 * @date 2022-08-08
 */
export default {
  name: 'CodeConfirmButton',
  // 父组件传入参数
  props: {
    previewCodes: {
      type: Array,
      default: () => []
    }
  },
  // 计算操作
  computed: {},
  data() {
    return {
      // 组件内部弹出框展示与否
      dialogVisible: false,
      checkedIds: [],
      isIndeterminate: false,
      checkAll: false,
    }
  },
  methods: {
    // 点击数据权限按钮
    clickCodeConfirmButton() {
      this.dialogVisible = true;
    },
    // 点击取消按钮
    clickCancel() {
      this.dialogVisible = false;
    },
    // 关闭弹窗时
    clickDialogClose() {
      this.dialogVisible = false;
    },
    // 点击全选
    handleCheckAllChange(val) {
      this.checkedIds = val ? this.previewCodes.map(item => item.key.split('/').pop()) : [];
      this.isIndeterminate = false;
    },
    // 核心修改：区分 Electron/浏览器环境，仅浏览器用 FSAA
    async clickConfirm() {
      const userAgent = navigator.userAgent.toLowerCase();

      // 1. Electron 环境：保持原有逻辑不变
      if (userAgent.indexOf('electron/') !== -1) {
        this.previewCodes.forEach(item => {
          const fileName = item.key.split('/').pop();
          const filePath = item.key.substring(0, item.key.length - fileName.length - 1);
          this.checkedIds.forEach(checkedItem => {
            if (fileName === checkedItem) {
              const data = {
                fileName: fileName,
                path: filePath,
                data: item.code
              }
              const isSuccess = window.appApi.writeFile(data);
              this.dialogVisible = !isSuccess;
              if (isSuccess) {
                Message.success("代码生成成功！")
              } else {
                Message.warning("代码生成失败！(可能与路径权限有关)")
              }
            }
          })
        })
      }
      // 2. 浏览器环境：使用 File System Access API（FSAA）
      else {
        try {
          // 检查浏览器是否支持 FSAA（部分浏览器需 HTTPS 环境）
          if (!window.showDirectoryPicker) {
            Message.error("当前浏览器不支持文件系统API，请使用最新版Chrome、Edge或Opera浏览器（需HTTPS环境）");
            return;
          }

          // 弹出目录选择框，让用户选择项目根目录（需读写权限）
          const rootDirHandle = await window.showDirectoryPicker({
            mode: 'readwrite',
            title: '选择项目根目录',
            startIn: 'documents'
          });

          let successCount = 0;
          let failFiles = [];

          // 遍历选中的文件，逐个写入
          for (const item of this.previewCodes) {
            const fileName = item.key.split('/').pop();
            if (!this.checkedIds.includes(fileName)) continue;

            // 计算相对路径（如：src/components）
            const relativePath = item.key.substring(0, item.key.length - fileName.length - 1);
            const pathParts = relativePath ? relativePath.split('/').filter(part => part) : [];

            try {
              // 递归创建多级目录
              const targetDirHandle = await this.createDirectoryRecursive(rootDirHandle, pathParts);
              // 创建/覆盖文件
              const fileHandle = await targetDirHandle.getFileHandle(fileName, {
                create: true,
                exclusive: false
              });

              // 写入文件内容
              const writable = await fileHandle.createWritable();
              await writable.write(item.code);
              await writable.close();

              successCount++;
            } catch (writeErr) {
              failFiles.push(fileName);
              console.error(`文件 ${fileName} 写入失败:`, writeErr);
            }
          }

          // 结果提示
          this.dialogVisible = false;
          if (successCount > 0) {
            Message.success(`代码生成成功！共生成 ${successCount} 个文件`);
          }
          if (failFiles.length > 0) {
            Message.error(`以下文件生成失败：${failFiles.join(', ')}（请检查目录权限）`);
          }

        } catch (dirErr) {
          // 处理目录选择异常
          if (dirErr.name === 'AbortError') {
            Message.info("已取消目录选择，代码生成终止");
          } else if (dirErr.name === 'SecurityError') {
            Message.error("权限不足！请允许浏览器访问文件系统，或使用HTTPS环境重试");
          } else {
            Message.error(`文件生成失败：${dirErr.message}`);
          }
          return;
        }
      }
    },
    // 辅助函数：递归创建多级目录（浏览器环境专用）
    async createDirectoryRecursive(rootHandle, pathParts) {
      let currentHandle = rootHandle;
      for (const part of pathParts) {
        try {
          currentHandle = await currentHandle.getDirectoryHandle(part, { create: true });
        } catch (err) {
          throw new Error(`创建目录 "${part}" 失败：${err.message}`);
        }
      }
      return currentHandle;
    },
    handleCheckedChange(value) {
      let checkedCount = value.length;
      this.checkAll = checkedCount === this.previewCodes.length;
      this.isIndeterminate = checkedCount > 0 && checkedCount < this.previewCodes.length;
    }
  },
  created() {

  },
  // mounted生命周期
  mounted() {

  },
  // 生命周期updated执行时
  updated() {

  }
}
</script>

<style lang="less" scoped>
/* tree border */
.tree-border {
  margin-top: 5px;
  border: 1px solid #e5e6e7;
  background: #FFFFFF none;
  border-radius: 4px;
}
::v-deep .el-dialog__body {
  padding-bottom: 26px;
}
</style>
